import Button from 'Components/Buttons/Button';
import { errorToast, successToast } from 'Components/Notifications';
import OverlayWindow from 'Components/OverlayWindow';
import { useCurrentPopup } from 'Components/Popups';
import { useConfirmDeleteModal } from 'Components/Popups/ConfirmDeletePopup';
import useCreateModal from 'Components/Popups/useCreateModal';
import Tooltip from 'Components/Tooltips';
import {
  IDirectoryNode,
  useDeleteNodeMutation,
  useMoveToTrashMutation,
  useRestoreMutation,
} from 'app/services/documents';
import {
  DeleteDocumentIcon,
  DownloadFileIcon,
  MoveToIcon,
  RestoreIcon,
} from 'assets/icons';
import saveAs from 'file-saver';
import JSZip from 'jszip';
import { useEffect, useState } from 'react';
import { useDocumentsContext } from '.';
import { useHavePermissions } from './DirectoryNodeDetails';
import { downloadFile } from './useActions';
import { BulkDownloadModal, useBulkDownloadModal } from './BulkDownloadModal';

// only delete root
const intersects = <T,>(array1: T[], array2: T[]) =>
  array1.filter((value) => array2.includes(value));

const prepare = (nodes: IDirectoryNode[]) =>
  nodes.map((d) => ({
    ...d,
    path: d.path
      .split('/')
      .slice(0, -1)
      .filter((d) => !!d)
      .map((d) => +d),
  }));

function deletableNodes(nodes: IDirectoryNode[]) {
  const candidates = nodes.map((d) => d.id);

  return prepare(nodes)
    .map((d) => ({
      id: d.id,
      skip: intersects(d.path, candidates).length > 0,
    }))
    .filter((d) => !d.skip)
    .map((d) => d.id);
}

export function firstCommonParent(nodes: IDirectoryNode[]): number | undefined {
  return _.last(
    prepare(nodes)
      .map((n) => n.path)
      .reduce((acc, cur) => intersects(acc, cur)),
  );
}

export default function MultiNodeActions() {
  const {
    GetPage,
    setFilters,
    unselectAll,
    MoveModal,
    RestoreModal,
    selected,
    filters,
    selectedCount,
  } = useDocumentsContext();
  const confirmDelete = useConfirmDeleteModal();
  const [DeleteTrigger] = useDeleteNodeMutation();
  const [NoveToTrashTrigger] = useMoveToTrashMutation();
  const [RestoreTrigger] = useRestoreMutation();
  const isAllowed = useHavePermissions();
  const DownloadProgressModal = useCreateModal();
  const BulkModal = useBulkDownloadModal();

  const selectedDocuments = Object.values(selected) || [];
  const hasFolder = selectedDocuments.some((d) => d.object_type === 'folder');
  const canDownload = selectedDocuments.every((d) => !!d.file_url) || hasFolder;
  const canMove = selectedDocuments.every(isAllowed);
  const canDelete = selectedDocuments.every(
    (d) => isAllowed(d) && d.is_trashed,
  );
  const canMoveToTrash = selectedDocuments.every(
    (d) => isAllowed(d) && !d.is_trashed,
  );

  const deleteNodes = () => {
    const ids = deletableNodes(selectedDocuments);

    confirmDelete(
      `${selectedDocuments.length} items`,
      () => {
        const promises = ids.map((id) => DeleteTrigger(id).unwrap());

        return Promise.allSettled(promises).then((results) => {
          const successes = results.filter(
            (result) => result.status === 'fulfilled',
          );
          const failures = results.filter(
            (result) => result.status === 'rejected',
          );
          if (successes.length > 0) {
            unselectAll();
            setFilters({
              ...filters,
              id: firstCommonParent(selectedDocuments),
            });

            successToast(`${successes.length} items moved to trash`);
          }
          if (failures.length > 0) {
            errorToast(`${failures.length} items failed to be deleted`);
          }
        });
      },
      true,
    );
  };

  const moveToTrash = () => {
    const ids = deletableNodes(selectedDocuments);

    const promises = ids.map((id) =>
      NoveToTrashTrigger({ id, destination_parent_id: null }).unwrap(),
    );
    return Promise.allSettled(promises).then((results) => {
      const successes = results.filter(
        (result) => result.status === 'fulfilled',
      );
      const failures = results.filter((result) => result.status === 'rejected');
      if (successes.length > 0) {
        unselectAll();
        setFilters({ ...filters, id: firstCommonParent(selectedDocuments) });
        successToast(`${successes.length} items moved to trash`);
      }
      if (failures.length > 0) {
        errorToast(`${failures.length} items failed to move to trash`);
      }
    });
  };

  const restoreNodes = () => {
    RestoreModal.openWithContext({ data: selectedDocuments });
  };

  const moveNodes = () => {
    MoveModal.openWithContext({ data: selectedDocuments });
  };

  const download = () => {
    const nodes = Object.values(selected);
    const docs = nodes.filter((d) => !!d.file_url);
    const folders = nodes.filter((d) => d.object_type === 'folder');

    const downloadFoldersSequentially = (index: number) => {
      if (index >= folders.length) return;
      const folder = folders[index];
      BulkModal.openWithContext({
        data: {
          id: folder.id,
          title: folder.title,
          onDone: () => downloadFoldersSequentially(index + 1),
        },
      });
    };

    if (folders.length > 0) {
      downloadFoldersSequentially(0);
    }

    docs.forEach((document) =>
      downloadFile(
        `${process.env.REACT_APP_URL}${document.file_url}`,
        document.title,
      ),
    );
    // DownloadProgressModal.openWithContext({
    //   data: Object.values(selected)
    //     .filter((d) => !!d.file_url)
    //     .map((d) => `${process.env.REACT_APP_URL}${d.file_url}`),
    // });
  };

  return (
    <>
      <div className="flex gap-2 mr-auto">
        <Tooltip message="Clear">
          <Button
            className="max-w-min"
            theme="red-outline"
            onClick={unselectAll}
          >
            {selectedCount} Selected
          </Button>
        </Tooltip>

        <Tooltip message="Download">
          <Button disabled={!canDownload} onClick={download}>
            <DownloadFileIcon className="h-5 w-6" />
          </Button>
        </Tooltip>
        {!filters.is_trashed && (
          <Tooltip message="Move">
            <Button disabled={!canMove} onClick={moveNodes}>
              <MoveToIcon className="h-5 w-6" />
            </Button>
          </Tooltip>
        )}

        {filters.is_trashed && (
          <Tooltip message="Restore">
            <Button disabled={!canMove} onClick={restoreNodes}>
              <RestoreIcon className="h-5 w-6" />
            </Button>
          </Tooltip>
        )}

        {filters.is_trashed && (
          <Tooltip message="Permanent Delete">
            <Button
              disabled={!canDelete}
              onClick={deleteNodes}
              theme="red-outline"
            >
              <DeleteDocumentIcon className="h-5 w-6" />
            </Button>
          </Tooltip>
        )}

        {!filters.is_trashed && (
          <Tooltip message={'Move To Trash'}>
            <Button
              theme={'red-outline'}
              onClick={moveToTrash}
              disabled={!canMoveToTrash}
            >
              <DeleteDocumentIcon className="h-5 w-5" />
            </Button>
          </Tooltip>
        )}
      </div>

      <DownloadProgressModal.Component>
        <DownloadModal />
      </DownloadProgressModal.Component>
      <BulkModal.Component>
        <BulkDownloadModal />
      </BulkModal.Component>
    </>
  );
}

function DownloadModal() {
  const { contextData: urls, close } = useCurrentPopup<string[]>();
  const [downloadZip, doneCount] = useDownloadZip(urls!);
  useEffect(() => {
    const handle = () => {
      downloadZip().then(() => {
        close();
      });
    };

    setTimeout(handle, 800);
  }, []);
  const totalCount = urls!.length;

  const progress = `${Math.min(doneCount / totalCount) * 100}%`;

  return (
    <>
      <OverlayWindow
        paddingSize="py-2 px-2"
        title={
          <div className="text-gray-700 text-sm">
            {doneCount} of {totalCount} downloaded
          </div>
        }
      >
        <div className="w-full mt-4 animate-fade bg-gray-200 rounded-full h-1.5 mb-4">
          <div
            className="bg-green h-1.5 rounded-full"
            style={{
              width: progress,
            }}
          />
        </div>
      </OverlayWindow>
    </>
  );
}

export const useDownloadZip = (urls: string[]) => {
  let filename = 'documents';

  const [remaining, setRemaining] = useState(() => urls.length);

  const zip = new JSZip();

  return [
    () => {
      const FileNames = new Set();
      urls.forEach((url, index) => {
        const blobPromise = fetch(url).then(function (response) {
          if (response.status === 200 || response.status === 0) {
            setRemaining((count) => count - 1);
            return Promise.resolve(response.blob());
          } else {
            return Promise.reject(new Error(response.statusText));
          }
        });
        let fileName = decodeURIComponent(url.substring(url.lastIndexOf('/')));

        if (FileNames.has(fileName)) {
          let ext = fileName.split('.').pop();
          fileName = `${fileName.replace(/\.[^/.]+$/, '')}_(${index}).${ext}`;
        }
        FileNames.add(fileName);

        zip?.file(fileName, blobPromise);
      });

      return zip
        .generateAsync({ type: 'blob' })
        .then((blob) => {
          saveAs(blob, filename);
          setRemaining(0);
        })
        .catch((e) => console.log(e));
    },
    urls.length - remaining,
  ] as const;
};

type setTimeoutReturnType = ReturnType<typeof setTimeout>;
